import { DEFAULT_COUNTS_PANEL_DATA } from 'features/drilldown/panels/global/countsView/DrilldownCountsView'
import {
  DrilldownExplorationConfiguration,
  DrilldownViewState,
} from 'features/drilldown/types'
import { buildUuid } from 'features/drilldown/utils/drilldownIdUtils'
import { useRolodexEntries } from 'features/rolodex'
import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useMemo,
} from 'react'
import { GeckoOrganization, RolodexEntryType } from 'types'

import { DEFAULT_COLUMN_WIDTH } from './explorationConstants'

export const DEFAULT_EMPTY_VIEW_STATE: DrilldownViewState = [
  {
    id: buildUuid(),
    panelType: 'counts',
    panelData: DEFAULT_COUNTS_PANEL_DATA,
    filters: new Map(),
    focusItems: undefined,
  },
]

export const DEFAULT_EMPTY_CONFIGURATION: DrilldownExplorationConfiguration = {
  columnWidths: [DEFAULT_COLUMN_WIDTH],
}

const EMPTY_DRILLDOWN_EXPLORATION_CONTEXT: DrilldownExplorationContextType = {
  orgId: null,
  siteId: null,
  explorationId: undefined,
  organization: null,
  explorationState: DEFAULT_EMPTY_VIEW_STATE,
  setExplorationState: () => {},
  configuration: DEFAULT_EMPTY_CONFIGURATION,
  setConfiguration: () => {},
  isLoading: false,
  isPresentationModeEnabled: false,
  setIsPresentationModeEnabled: () => {},
}

export interface DrilldownExplorationContextType {
  orgId: string | null
  siteId: string | null
  /** Stable id for this saved or unsaved exploration. */
  explorationId?: string
  organization: GeckoOrganization | null
  explorationState: DrilldownViewState
  setExplorationState: Dispatch<SetStateAction<DrilldownViewState>>
  configuration: DrilldownExplorationConfiguration
  setConfiguration: Dispatch<SetStateAction<DrilldownExplorationConfiguration>>
  isPresentationModeEnabled: boolean
  setIsPresentationModeEnabled: Dispatch<SetStateAction<boolean>>
  isLoading: boolean
  explorationTitle?: string
}

export const DrilldownDataContext =
  createContext<DrilldownExplorationContextType>(
    EMPTY_DRILLDOWN_EXPLORATION_CONTEXT
  )

export const DrilldownExplorationProvider: FC<
  { children: ReactNode } & DrilldownExplorationProviderProps
> = ({ children, ...rest }) => {
  const data = useDrilldownExplorationData(rest)
  const context = useMemo<DrilldownExplorationContextType>(
    () => ({
      ...data,
    }),
    [data]
  )

  return (
    <DrilldownDataContext.Provider value={context}>
      {children}
    </DrilldownDataContext.Provider>
  )
}

export interface DrilldownExplorationProviderProps {
  orgId: string | null
  siteId: string | null
  explorationId?: string
  isPresentationModeEnabled: boolean
  setIsPresentationModeEnabled?: Dispatch<SetStateAction<boolean>>
  explorationState: DrilldownViewState
  setExplorationState: Dispatch<SetStateAction<DrilldownViewState>>
  configuration: DrilldownExplorationConfiguration
  setConfiguration: Dispatch<SetStateAction<DrilldownExplorationConfiguration>>
  isLoading: boolean
  clearState: (orgId: string | null, siteId: string | null) => void
  explorationTitle?: string
}

function useDrilldownExplorationData({
  orgId,
  siteId,
  explorationId,
  isPresentationModeEnabled,
  setIsPresentationModeEnabled,
  explorationState,
  setExplorationState,
  configuration,
  setConfiguration,
  explorationTitle,
}: DrilldownExplorationProviderProps): DrilldownExplorationContextType {
  const { organizations, isLoading: orgsIsLoading } = useRolodexEntries({
    type: RolodexEntryType.SITE,
    links: [RolodexEntryType.ORGANIZATION],
    limit: 100000,
  })

  return useMemo(
    () => ({
      // The orgId will line up with the organization, but
      // the organization has to load, so if you depend on it
      // it may cause a flicker
      orgId: orgId ?? null,
      siteId: siteId ?? null,
      explorationId,
      organization: organizations.length === 1 ? organizations?.[0] : null,
      isLoading: orgsIsLoading,
      explorationState,
      setExplorationState,
      configuration,
      setConfiguration,
      isPresentationModeEnabled,
      setIsPresentationModeEnabled: setIsPresentationModeEnabled ?? (() => {}),
      explorationTitle,
    }),
    [
      explorationState,
      setExplorationState,
      configuration,
      setConfiguration,
      isPresentationModeEnabled,
      orgId,
      siteId,
      explorationId,
      organizations,
      orgsIsLoading,
      setIsPresentationModeEnabled,
      explorationTitle,
    ]
  )
}
